import type { Dayjs } from 'dayjs';

export type SelectedDateRangeOption = {
  value: string;
  label: string;
  from: Dayjs;
  to: Dayjs;
};

const toOptionValue = (from: Dayjs, to: Dayjs) =>
  from.isSame(to, 'day')
    ? from.format('YYYY-MM-DD')
    : `${from.format('YYYY-MM-DD')}_${to.format('YYYY-MM-DD')}`;

export const formatSelectedDateRangeLabel = (from: Dayjs, to: Dayjs): string => {
  if (from.isSame(to, 'day')) {
    return from.format('DD MMM YY');
  }

  if (from.isSame(to, 'month') && from.isSame(to, 'year')) {
    return `${from.format('DD')} - ${to.format('DD MMM YY')}`;
  }

  if (from.isSame(to, 'year')) {
    return `${from.format('DD MMM')} - ${to.format('DD MMM YY')}`;
  }

  return `${from.format('DD MMM YY')} - ${to.format('DD MMM YY')}`;
};

export const groupSelectedDatesIntoRanges = (dates: Dayjs[]): SelectedDateRangeOption[] => {
  if (dates.length === 0) {
    return [];
  }

  const sortedDates = [...dates]
    .map((date) => date.startOf('day'))
    .sort((a, b) => a.valueOf() - b.valueOf());

  const groups: Dayjs[][] = [];
  let currentGroup: Dayjs[] = [sortedDates[0]];

  for (let index = 1; index < sortedDates.length; index += 1) {
    const date = sortedDates[index];
    const previousDate = currentGroup[currentGroup.length - 1];

    if (date.diff(previousDate, 'day') === 1) {
      currentGroup.push(date);
    } else {
      groups.push(currentGroup);
      currentGroup = [date];
    }
  }

  groups.push(currentGroup);

  return groups.map((group) => {
    const from = group[0];
    const to = group[group.length - 1];

    return {
      value: toOptionValue(from, to),
      label: formatSelectedDateRangeLabel(from, to),
      from,
      to,
    };
  });
};

export const findSelectedDateRangeOption = (
  options: SelectedDateRangeOption[],
  value?: string
) => options.find((option) => option.value === value);
